import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Shield, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { email: "", name: "", password: "", role: "admin" };

export default function AdminUsers() {
  const { user: current } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admins").then((r) => setItems(r.data)).catch((e) => {
    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chargement impossible");
  });

  useEffect(() => { load(); }, []);

  const upd = (k) => (v) => {
    const val = v?.target ? v.target.value : v;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, name: u.name, password: "", role: u.role }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const body = { name: form.name, role: form.role };
        if (form.password) body.password = form.password;
        await api.put(`/admins/${editing.id}`, body);
        toast.success("Compte mis à jour");
      } else {
        await api.post("/admins", form);
        toast.success("Compte administrateur créé");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer le compte de ${u.email} ?`)) return;
    try {
      await api.delete(`/admins/${u.id}`);
      toast.success("Compte supprimé");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-gold-dark text-xs tracking-[0.3em] uppercase">Sécurité</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-deepblue-900 mt-1">Administrateurs</h1>
          <p className="text-slate-500 mt-2">Gérez les comptes qui peuvent administrer le site.</p>
        </div>
        <Button onClick={openCreate} className="bg-gold hover:bg-gold-hover text-white rounded-full" data-testid="add-admin-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouvel admin
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rôle</TableHead>
              <TableHead>Créé le</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">Aucun compte</TableCell></TableRow>}
            {items.map((u) => {
              const isSelf = current?.id === u.id;
              const isSuper = u.role === "super_admin";
              return (
                <TableRow key={u.id} data-testid={`admin-row-${u.id}`}>
                  <TableCell className="font-medium text-deepblue-900">
                    {u.name} {isSelf && <span className="text-xs text-slate-400 ml-1">(vous)</span>}
                  </TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded font-semibold ${isSuper ? "bg-gold/20 text-gold-dark" : "bg-slate-100 text-slate-700"}`}>
                      {isSuper ? <ShieldCheck className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                      {isSuper ? "Super admin" : "Admin"}
                    </span>
                  </TableCell>
                  <TableCell>{u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-admin-${u.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(u)} disabled={isSelf} data-testid={`delete-admin-${u.id}`}>
                      <Trash2 className={`w-4 h-4 ${isSelf ? "text-slate-300" : "text-red-600"}`} />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Modifier le compte" : "Nouveau compte administrateur"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom complet *</Label>
              <Input value={form.name} onChange={upd("name")} className="mt-1.5" data-testid="admin-form-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={upd("email")} disabled={!!editing}
                className="mt-1.5" data-testid="admin-form-email"
                placeholder="prenom@canaanmeigda.com" />
              {editing && <p className="text-xs text-slate-400 mt-1">L'email ne peut pas être modifié.</p>}
            </div>
            <div>
              <Label>{editing ? "Nouveau mot de passe (laisser vide pour ne pas changer)" : "Mot de passe *"}</Label>
              <Input type="password" value={form.password} onChange={upd("password")}
                className="mt-1.5" data-testid="admin-form-password"
                placeholder="Minimum 6 caractères" />
            </div>
            <div>
              <Label>Rôle</Label>
              <Select value={form.role} onValueChange={upd("role")}>
                <SelectTrigger className="mt-1.5" data-testid="admin-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — gère le contenu</SelectItem>
                  <SelectItem value="super_admin">Super admin — gère tout, y compris les comptes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-gold hover:bg-gold-hover text-white" data-testid="save-admin-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
